// W02 / W03 / W04 — jelszóemlékeztető, betöltő és hibaoldal.
//
// A belépés körüli három „mellékképernyő”. Mindhárom önállóan áll: nem
// ismerik a routert és nem nyúlnak a Firebase-hez a kapott példányokon
// túl — a továbblépésről a hívó (main.ts) dönt a callbackeken keresztül.
//
// A márkapanel szándékosan itt is szerepel (a login.ts-ben lévő másolata
// helyett): a két fájl egymástól függetlenül szerkeszthető, és a panel
// néhány statikus elem, nem üzleti logika.

import { sendPasswordResetEmail, type Auth } from 'firebase/auth';
import { t } from '../lib/i18n';
import { h, icon, icons } from '../lib/dom';

/// A tervekből átemelt, csak itt használt ikonok.
///
/// Egyetlen `path`-ba írva, mert a `dom.ts` `icon()` segédje egy útvonalat
/// rajzol — több részútvonal ugyanabban a `d` attribútumban elfér.
const extraIcons = {
  check: 'M3 8.5l3.2 3.2L13 5',
  lock: 'M4.4 7h7.2v6.4H4.4Z M5.6 7V5.2a2.4 2.4 0 0 1 4.8 0V7',
  building: 'M3 13.6V3.2h6.2v10.4 M9.2 6.8H13v6.8 M5.2 5.6h2 M5.2 8h2 M5.2 10.4h2',
  wifiOff: 'M1.8 5.6a10 10 0 0 1 12.4 0 M4.4 8.4a6 6 0 0 1 7.2 0 M6.9 11.2a2.2 2.2 0 0 1 2.2 0 M2 2l12 12',
} as const;

/// Igaz, ha a felhasználó nem kért csökkentett mozgást.
///
/// Az app.css a CSS-animációkat már letiltja ilyenkor; a JS-ből indított
/// (Web Animations API) mozgásra ez nem vonatkozik, ezért kérdezzük meg.
function motionAllowed(): boolean {
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/// A Firebase hibakódjainak magyar megfelelője (jelszó-levél küldésekor).
///
/// A nyers kódot ("auth/invalid-email") sosem mutatjuk a felhasználónak.
function resetErrorMessage(code: string): string {
  switch (code) {
    case 'auth/invalid-email':
      return t('auth.invalidEmail');
    case 'auth/missing-email':
      return t('auth.provideEmail');
    case 'auth/too-many-requests':
      return t('auth.resetTooManyRequests');
    case 'auth/network-request-failed':
      return t('auth.networkError');
    default:
      return t('auth.resetUnknownError');
  }
}

/// A belépő bal oldali akcentus-panelje (W01/W02 azonos).
function brandPanel(): HTMLElement {
  return h(
    'aside',
    {
      class: 'brand-panel',
      style: {
        background: 'var(--accent)',
        color: 'var(--on-accent)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '48px',
      },
    },
    h(
      'span',
      { class: 'logo', style: { fontSize: '18px', color: 'var(--on-accent)' } },
      h('span', { class: 'mk', style: { background: 'rgba(255,255,255,.18)' } }, icon(icons.spark)),
      'Blaze',
    ),
    h(
      'div',
      { class: 'col', style: { gap: '18px' } },
      h('h2', {
        class: 'display',
        style: {
          fontSize: '44px',
          color: 'var(--on-accent)',
          whiteSpace: 'pre-line',
        },
        text: t('auth.brandHeadline'),
      }),
      h('p', {
        class: 'body-lg',
        style: { maxWidth: '400px', opacity: '.92' },
        text: t('auth.brandLead'),
      }),
    ),
    h(
      'div',
      { class: 'row g12', style: { opacity: '.9' } },
      h('span', { class: 'ic' }, icon(icons.shield)),
      h('span', {
        class: 'body-sm',
        text: t('auth.brandOffline'),
      }),
    ),
  );
}

/// Kerek ikoncsempe a hibaoldal élén (W04).
function iconTile(path: string, tone: 'warn' | 'err' | 'info'): HTMLElement {
  return h(
    'span',
    {
      class: 'ic',
      style: {
        width: '64px',
        height: '64px',
        borderRadius: '18px',
        background: `var(--${tone}-soft)`,
        color: `var(--${tone})`,
      },
    },
    icon(path, 28),
  );
}

/// Címke–érték sor a hibaoldal adatkártyájában (W04).
function detailRow(label: string, value: string, mono = false): HTMLElement {
  return h(
    'div',
    {
      class: 'row top g12',
      style: { padding: '7px 0', borderBottom: '1px solid var(--border)' },
    },
    h('span', { class: 'caption', style: { width: '140px', flex: 'none' }, text: label }),
    h('span', {
      class: mono ? 'body-sm mono' : 'body-sm',
      style: { flexGrow: '1', minWidth: '0', overflowWrap: 'anywhere' },
      text: value,
    }),
  );
}

// ---------------------------------------------------------------------------
// W02 — Jelszóemlékeztető
// ---------------------------------------------------------------------------

/// Önálló jelszóemlékeztető oldal.
///
/// A `onBack` visszavisz a belépésre — ezért valódi `button`, nem link: a
/// navigációt a hívó intézi, nincs önálló URL-je a képernyőnek.
export function passwordResetPage(options: {
  auth: Auth;
  onBack: () => void;
}): HTMLElement {
  const { auth, onBack } = options;

  const emailInput = h('input', {
    class: 'input',
    type: 'email',
    id: 'reset-email',
    autocomplete: 'username',
    placeholder: t('auth.emailPlaceholder'),
    required: 'true',
  });

  const errorBox = h('div', {
    class: 'row top g12',
    role: 'alert',
    style: {
      display: 'none',
      padding: '12px 14px',
      borderRadius: '10px',
      background: 'var(--err-soft)',
      border: '1px solid var(--s-rej-bd)',
    },
  });

  function showError(message: string): void {
    errorBox.replaceChildren(
      h('span', { class: 'ic', style: { color: 'var(--err)' } }, icon(icons.warning)),
      h('span', { class: 'body-sm w6', style: { color: 'var(--err)' }, text: message }),
    );
    errorBox.style.display = 'flex';
    // A hibaüzenetre fókuszálunk, hogy a képernyőolvasó is felolvassa.
    errorBox.setAttribute('tabindex', '-1');
    errorBox.focus();
  }

  function hideError(): void {
    errorBox.style.display = 'none';
  }

  /// A sikeres küldést visszaigazoló kártya (W02). Küldés előtt rejtve van.
  const sentCard = h(
    'div',
    {
      class: 'card pad20 row top g12',
      role: 'status',
      'aria-live': 'polite',
      style: { display: 'none' },
    },
    h(
      'span',
      {
        class: 'ic',
        style: {
          width: '34px',
          height: '34px',
          borderRadius: '10px',
          background: 'var(--ok-soft)',
          color: 'var(--ok)',
        },
      },
      icon(extraIcons.check, 18),
    ),
    h(
      'span',
      { class: 'col g4' },
      h('span', { class: 'body w6', text: t('auth.resetSentTitle') }),
      h('span', {
        class: 'body-sm ts',
        text: t('auth.resetSentBody'),
      }),
    ),
  );

  const submit = h('button', {
    class: 'btn btn-primary btn-lg',
    type: 'submit',
    style: { width: '100%' },
    text: t('auth.resetSubmit'),
  });

  let busy = false;
  function setBusy(value: boolean): void {
    busy = value;
    submit.disabled = value;
    submit.textContent = value ? t('auth.sending') : t('auth.resetSubmit');
  }

  const form = h(
    'form',
    {
      class: 'col g16',
      novalidate: 'true',
      onSubmit: async (event: Event) => {
        event.preventDefault();
        if (busy) return;
        hideError();
        sentCard.style.display = 'none';

        const email = emailInput.value.trim();
        if (!email) {
          showError(t('auth.provideEmailForReset'));
          emailInput.focus();
          return;
        }

        setBusy(true);
        try {
          await sendPasswordResetEmail(auth, email);
          sentCard.style.display = 'flex';
        } catch (error) {
          const code = (error as { code?: string }).code ?? '';
          // Nem áruljuk el, hogy létezik-e fiók a megadott címmel: ismeretlen
          // címre ugyanazt a visszaigazolást mutatjuk, mint sikeres küldéskor.
          if (code === 'auth/user-not-found') {
            sentCard.style.display = 'flex';
          } else {
            showError(resetErrorMessage(code));
          }
        } finally {
          setBusy(false);
        }
      },
    },
    h(
      'div',
      { class: 'field' },
      h('label', { class: 'flbl', for: 'reset-email', text: t('auth.email') }),
      emailInput,
      h('span', {
        class: 'hint',
        text: t('auth.resetEmailHint'),
      }),
    ),
    submit,
    h('button', {
      class: 'btn btn-ghost',
      type: 'button',
      style: { width: '100%' },
      text: t('auth.backToLogin'),
      onClick: () => onBack(),
    }),
  );

  return h(
    'div',
    { class: 'login-layout' },
    brandPanel(),
    h(
      'main',
      {
        style: {
          flexGrow: '1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 16px',
        },
      },
      h(
        'div',
        { class: 'col', style: { width: '420px', maxWidth: '100%', gap: '22px' } },
        h(
          'div',
          { class: 'col', style: { gap: '6px' } },
          h('h1', { class: 'h1', text: t('auth.resetTitle') }),
          h('p', {
            class: 'body ts',
            text: t('auth.resetSubtitle'),
          }),
        ),
        errorBox,
        form,
        sentCard,
      ),
    ),
  );
}

// ---------------------------------------------------------------------------
// W03 — Betöltő
// ---------------------------------------------------------------------------

/// Betöltő képernyő (munkamenet visszaállítása).
///
/// A `role="status"` + `aria-live="polite"` miatt a képernyőolvasó is
/// megtudja, hogy dolgozunk — anélkül, hogy félbeszakítaná a felhasználót.
export function loadingPage(): HTMLElement {
  const ring = h('div', { class: 'ring' });
  const barFill = h('i', { style: { width: '38%' } });

  // A ds.css a gyűrűt statikusan rajzolja (a tervek állóképek). A forgatást
  // és a határozatlan sávot itt indítjuk, hogy ne kelljen a közös
  // stíluslaphoz nyúlni — csökkentett mozgásnál pedig egyszerűen elmarad.
  if (motionAllowed()) {
    ring.animate(
      [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
      { duration: 900, iterations: Infinity, easing: 'linear' },
    );
    barFill.animate(
      [{ transform: 'translateX(-100%)' }, { transform: 'translateX(300%)' }],
      { duration: 1500, iterations: Infinity, easing: 'ease-in-out' },
    );
  }

  return h(
    'div',
    {
      role: 'status',
      'aria-live': 'polite',
      style: {
        flexGrow: '1',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '18px',
        padding: '32px',
      },
    },
    h(
      'span',
      { class: 'logo', style: { fontSize: '18px' } },
      h('span', { class: 'mk' }, icon(icons.spark)),
      'Blaze',
    ),
    ring,
    h(
      'div',
      { class: 'col', style: { alignItems: 'center', gap: '4px' } },
      h('span', { class: 'body w6', text: t('auth.loadingTitle') }),
      h('span', {
        class: 'caption',
        text: t('auth.loadingSubtitle'),
      }),
    ),
    h('div', { class: 'prog', style: { width: '220px' } }, barFill),
  );
}

// ---------------------------------------------------------------------------
// W04 — Hibaoldal
// ---------------------------------------------------------------------------

/// A hibaoldal esetei. Mindegyikhez más magyarázat és más kiút tartozik.
export type ErrorKind = 'no-company' | 'no-permission' | 'network' | 'unknown';

type ErrorCopy = {
  icon: string;
  tone: 'warn' | 'err' | 'info';
  /// SZÖVEG HELYETT KULCS. Ez a tábla modul szintű konstans, tehát a fájl
  /// betöltésekor áll össze — a szótár viszont csak a render előtt érkezik
  /// meg. Ha itt kész szöveg állna, a nyers kulcsokkal fagyna be.
  titleKey: string;
  bodyKey: string;
  /// A kártya „Mit tehetsz” sora: a konkrét következő lépés.
  adviceKey: string;
  /// A hibakód NEM fordítandó: pont az a dolga, hogy minden nyelven
  /// ugyanúgy nézzen ki, amikor a felhasználó bediktálja a támogatásnak.
  code: string;
  retryKey: string;
  signOutKey: string;
  /// Melyik gomb legyen az elsődleges, ha mindkettő elérhető.
  primary: 'retry' | 'signOut';
};

const errorCopy: Record<ErrorKind, ErrorCopy> = {
  'no-company': {
    icon: extraIcons.building,
    tone: 'warn',
    titleKey: 'error.noCompanyTitle',
    bodyKey: 'error.noCompanyBody',
    adviceKey: 'error.noCompanyAdvice',
    code: 'NO_COMPANY · 403',
    retryKey: 'error.recheck',
    signOutKey: 'error.signInOther',
    primary: 'signOut',
  },
  'no-permission': {
    icon: extraIcons.lock,
    tone: 'err',
    titleKey: 'error.noPermissionTitle',
    bodyKey: 'error.noPermissionBody',
    adviceKey: 'error.noPermissionAdvice',
    code: 'NO_PERMISSION · 403',
    retryKey: 'error.retry',
    signOutKey: 'error.signOut',
    primary: 'signOut',
  },
  network: {
    icon: extraIcons.wifiOff,
    tone: 'info',
    titleKey: 'error.networkTitle',
    bodyKey: 'error.networkBody',
    adviceKey: 'error.networkAdvice',
    code: 'NETWORK · offline',
    retryKey: 'error.retry',
    signOutKey: 'error.signOut',
    primary: 'retry',
  },
  unknown: {
    icon: icons.warning,
    tone: 'err',
    titleKey: 'error.unknownTitle',
    bodyKey: 'error.unknownBody',
    adviceKey: 'error.unknownAdvice',
    code: 'UNKNOWN · 500',
    retryKey: 'error.retry',
    signOutKey: 'error.signOut',
    primary: 'retry',
  },
};

/// Hibaoldal: cím, magyarázat és kiút.
///
/// A `onRetry` elhagyható — ha nincs, csak a kijelentkezés marad, mert
/// tétlen gombot nem mutatunk.
export function errorPage(options: {
  kind: ErrorKind;
  onRetry?: () => void;
  onSignOut: () => void;
  /// A belépett fiók címe. Ha nincs (pl. hálózati hiba belépés előtt), a sor
  /// kimarad — nem írunk oda kitalált értéket.
  email?: string;
  /// A fiók szerepköre. `null`/hiányzó esetén a terv szerinti „nincs
  /// beállítva" áll itt, mert a `no-company` hiba épp ettől jön.
  role?: string;
}): HTMLElement {
  const { kind, onRetry, onSignOut, email, role } = options;
  const copy = errorCopy[kind];

  // Ha az elsődleges kiút az újrapróbálás lenne, de nincs kezelő hozzá, a
  // kijelentkezés lép elő — így mindig pontosan egy hangsúlyos gomb van.
  const retryIsPrimary = copy.primary === 'retry' && onRetry !== undefined;

  const buttons: HTMLElement[] = [];
  if (onRetry) {
    buttons.push(
      h('button', {
        class: retryIsPrimary ? 'btn btn-primary' : 'btn btn-secondary',
        type: 'button',
        style: { flexGrow: '1' },
        text: t(copy.retryKey),
        onClick: () => onRetry(),
      }),
    );
  }
  buttons.push(
    h('button', {
      class: retryIsPrimary ? 'btn btn-secondary' : 'btn btn-primary',
      type: 'button',
      style: { flexGrow: '1' },
      text: t(copy.signOutKey),
      onClick: () => onSignOut(),
    }),
  );

  return h(
    'div',
    {
      role: 'alert',
      style: {
        flexGrow: '1',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
        padding: '32px',
      },
    },
    iconTile(copy.icon, copy.tone),
    h(
      'div',
      {
        class: 'col',
        style: { alignItems: 'center', gap: '8px', textAlign: 'center', maxWidth: '460px' },
      },
      h('h1', { class: 'h1', text: t(copy.titleKey) }),
      h('p', { class: 'body ts', text: t(copy.bodyKey) }),
    ),
    // A terv adatkártyája: a hibakód a BELÉPETT CÍM és a szerepkör mellett
    // áll. Támogatásnál pontosan ez a három adat kell egyszerre, és így az is
    // látszik, MELYIK fiókkal van a baj — korábban csak a kód szerepelt.
    h(
      'div',
      {
        class: 'card pad16 col',
        style: { width: '100%', maxWidth: '460px', gap: '6px' },
      },
      ...(email ? [detailRow(t('error.signedInAs'), email)] : []),
      detailRow(t('error.roleLabel'), role && role.trim() ? role : t('error.roleUnset')),
      detailRow(t('error.codeLabel'), copy.code, true),
    ),
    h(
      'div',
      { class: 'row g12', style: { width: '100%', maxWidth: '460px' } },
      ...buttons,
    ),
    // A „mit tehetsz" mondat a gombok ALÁ került: a gombok maguk a művelet,
    // a mondat csak magyarázza őket.
    h('p', {
      class: 'caption',
      style: { textAlign: 'center', maxWidth: '460px' },
      text: t(copy.adviceKey),
    }),
  );
}
